import { exportClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { ApiError } from '~/api/errors';
import { extractDispositionFilename, triggerBrowserDownload } from '~/utils/download-file';

import type {
  InsightMembershipCategory,
  InsightMembershipQuery,
} from '../types/catalog-operations.api.types';

/** export-service builds the file server-side (paging the membership read), so it
 *  can take longer than the default Ky timeout — same budget as feed-audit's. */
const EXPORT_TIMEOUT_MS = 120_000;

/** Row cap export-service enforces (`DRAWER_EXPORT_MAX_ROWS`). Mirrored here only
 *  to disable the button up front; the server's 413 stays authoritative. */
export const MEMBERSHIP_EXPORT_MAX_ROWS = 5_000;

export type MembershipExportFormat = 'csv' | 'tsv';

/** The drawer's current filters — the same params its list query sends, minus paging. */
export interface MembershipExportParams {
  workflowId: string;
  category: InsightMembershipCategory;
  /** Sent exactly as the card row carried it; the backend owns normalization. */
  label: string;
  /** Selected chip; `'ALL'` is omitted from the request, as in the list query. */
  group: NonNullable<InsightMembershipQuery['group']> | 'ALL';
  productCategory?: string;
  format?: MembershipExportFormat;
}

/** What was saved: the filename and the server's row count (`null` if not sent). */
export interface MembershipExportResult {
  filename: string;
  rowCount: number | null;
}

/** Thrown for a 413 — the list is over the export cap. Carries the numbers for the toast. */
export class MembershipExportTooLargeError extends Error {
  readonly totalElements: number | null;
  readonly maxRows: number;

  constructor(totalElements: number | null, maxRows: number) {
    super('Membership export exceeds the row cap');
    this.name = 'MembershipExportTooLargeError';
    this.totalElements = totalElements;
    this.maxRows = maxRows;
  }
}

/** Reads `{ totalElements, maxRows }` off a 413 body, flat or under FastAPI's `detail`. */
async function readTooLargeBody(
  error: ApiError,
): Promise<{ totalElements: number | null; maxRows: number }> {
  try {
    const raw = (await error.response.clone().json()) as Record<string, unknown>;
    const body = (raw.detail && typeof raw.detail === 'object' ? raw.detail : raw) as Record<
      string,
      unknown
    >;
    return {
      totalElements: typeof body.totalElements === 'number' ? body.totalElements : null,
      maxRows: typeof body.maxRows === 'number' ? body.maxRows : MEMBERSHIP_EXPORT_MAX_ROWS,
    };
  } catch {
    return { totalElements: null, maxRows: MEMBERSHIP_EXPORT_MAX_ROWS };
  }
}

/**
 * Downloads the full product list behind one insight row as CSV/TSV. Goes through
 * `exportClient` so the bearer + `X-Subsidiary-ID` interceptors run (a plain
 * `<a href>` can't send them), then saves the blob via a synthetic anchor. A 413 is
 * rethrown as `MembershipExportTooLargeError`; every other failure surfaces as the
 * interceptor's `ApiError`.
 */
export async function downloadInsightMembershipExport(
  params: MembershipExportParams,
): Promise<MembershipExportResult> {
  const { workflowId, category, label, group, productCategory, format = 'csv' } = params;
  const searchParams: Record<string, string> = { category, label, format };
  if (group !== 'ALL') searchParams.group = group;
  if (productCategory) searchParams.productCategory = productCategory;

  let response: Awaited<ReturnType<typeof exportClient.get>>;
  try {
    response = await exportClient.get(endpoints.insightMembershipExport.products(workflowId), {
      searchParams,
      timeout: EXPORT_TIMEOUT_MS,
    });
  } catch (error) {
    if (error instanceof ApiError && error.statusCode === 413) {
      const { totalElements, maxRows } = await readTooLargeBody(error);
      throw new MembershipExportTooLargeError(totalElements, maxRows);
    }
    throw error;
  }

  const blob = await response.blob();
  const filename =
    extractDispositionFilename(response.headers.get('content-disposition')) ??
    `${label}-${group.toLowerCase()}.${format}`;
  triggerBrowserDownload(blob, filename);

  const count = response.headers.get('x-export-row-count');
  const rowCount = count === null ? null : Number(count);
  return { filename, rowCount: Number.isFinite(rowCount) ? rowCount : null };
}
